// DS — `useConfirm()`: Promise<boolean> nad `ConfirmDialog`, zamiast `window.confirm` (G8-06).
//
//   const confirm = useConfirm()
//   if (!(await confirm({ title: t('…deleteTitle', { name }), body: …, confirmLabel: t('…delete'),
//                          cancelLabel: t('…keep') }))) return
//
// Jeden provider w main.tsx (i w harnessie); poza providerem hook zwraca funkcję,
// która odpowiada `false` i loguje — test komponentu nie potrzebuje providera,
// a produkt nigdy nie wpadnie w okno systemowe.

import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { ConfirmDialog, type ConfirmDialogProps } from './ConfirmDialog'

export type ConfirmOptions = Omit<ConfirmDialogProps, 'onConfirm' | 'onCancel' | 'busy'>

type Ask = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmCtx = createContext<Ask | null>(null)

type Pending = { opts: ConfirmOptions; resolve: (ok: boolean) => void }

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null)

  const ask = useCallback<Ask>((opts) => new Promise<boolean>((resolve) => {
    setPending({ opts, resolve })
  }), [])

  const settle = (ok: boolean) => {
    pending?.resolve(ok)
    setPending(null)
  }

  const value = useMemo(() => ask, [ask])

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      {pending ? (
        <ConfirmDialog {...pending.opts} onConfirm={() => settle(true)} onCancel={() => settle(false)} />
      ) : null}
    </ConfirmCtx.Provider>
  )
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmCtx)
  return ask ?? (async (opts) => {
    console.warn('[ds] useConfirm outside <ConfirmProvider>: answering false for', opts.title)
    return false
  })
}
